/**
 * Обрезка аудиофайла до N секунд в браузере (WebAudio + WAV-энкодер).
 * Файл короче лимита возвращается как есть, без перекодирования.
 */

export interface TrimAudioResult {
	file: globalThis.File;
	/** Длительность исходного файла, с. */
	duration: number;
	trimmed: boolean;
}

const MAX_CHANNELS = 2;

function writeAscii(view: DataView, offset: number, text: string) {
	for (let i = 0; i < text.length; i += 1) {
		view.setUint8(offset + i, text.charCodeAt(i));
	}
}

/** PCM 16-bit little-endian WAV из массивов сэмплов (-1..1) по каналам. */
export function encodeWav(
	channels: Float32Array[],
	sampleRate: number,
): ArrayBuffer {
	const channelCount = Math.max(1, channels.length);
	const frames = channels[0]?.length ?? 0;
	const bytesPerSample = 2;
	const blockAlign = channelCount * bytesPerSample;
	const dataSize = frames * blockAlign;

	const buffer = new ArrayBuffer(44 + dataSize);
	const view = new DataView(buffer);

	writeAscii(view, 0, "RIFF");
	view.setUint32(4, 36 + dataSize, true);
	writeAscii(view, 8, "WAVE");
	writeAscii(view, 12, "fmt ");
	view.setUint32(16, 16, true);
	view.setUint16(20, 1, true); // PCM
	view.setUint16(22, channelCount, true);
	view.setUint32(24, sampleRate, true);
	view.setUint32(28, sampleRate * blockAlign, true);
	view.setUint16(32, blockAlign, true);
	view.setUint16(34, bytesPerSample * 8, true);
	writeAscii(view, 36, "data");
	view.setUint32(40, dataSize, true);

	let offset = 44;
	for (let i = 0; i < frames; i += 1) {
		for (let c = 0; c < channelCount; c += 1) {
			const sample = Math.max(-1, Math.min(1, channels[c]?.[i] ?? 0));
			view.setInt16(
				offset,
				sample < 0 ? sample * 0x8000 : sample * 0x7fff,
				true,
			);
			offset += bytesPerSample;
		}
	}

	return buffer;
}

function getAudioContextCtor(): typeof AudioContext | null {
	const w = window as any;
	return w.AudioContext || w.webkitAudioContext || null;
}

export async function decodeAudio(file: Blob): Promise<AudioBuffer> {
	const Ctor = getAudioContextCtor();
	if (!Ctor) throw new Error("WebAudio is not supported");

	const ctx = new Ctor();
	try {
		const data = await file.arrayBuffer();
		// Колбэк-форма — для Safari, где promise-вариант появился поздно.
		return await new Promise<AudioBuffer>((resolve, reject) => {
			ctx.decodeAudioData(data, resolve, (err) =>
				reject(err ?? new Error("decodeAudioData failed")),
			);
		});
	} finally {
		ctx.close?.().catch?.(() => undefined);
	}
}

export function replaceExtension(name: string, ext: string) {
	const dot = name.lastIndexOf(".");
	const base = dot > 0 ? name.slice(0, dot) : name;
	return `${base}.${ext}`;
}

export async function trimAudioFile(
	file: globalThis.File,
	maxSeconds: number,
): Promise<TrimAudioResult> {
	const decoded = await decodeAudio(file);

	if (decoded.duration <= maxSeconds) {
		return { file, duration: decoded.duration, trimmed: false };
	}

	const frames = Math.min(
		decoded.length,
		Math.floor(maxSeconds * decoded.sampleRate),
	);
	const channelCount = Math.min(decoded.numberOfChannels, MAX_CHANNELS);
	const channels: Float32Array[] = [];
	for (let c = 0; c < channelCount; c += 1) {
		channels.push(decoded.getChannelData(c).subarray(0, frames));
	}

	const wav = encodeWav(channels, decoded.sampleRate);
	const trimmed = new globalThis.File(
		[wav],
		replaceExtension(file.name, "wav"),
		{ type: "audio/wav" },
	);

	return { file: trimmed, duration: decoded.duration, trimmed: true };
}
